import React, { useEffect, useRef, useState } from "react";
import {
  getVerificationUriComplete,
  signinForParameters,
} from "../../redux/Hooks/Auth/useDeviceSignin";
import setParametersOpeningAction from "../../redux/Actions/Auth/setParametersOpeningAction";
import QRCode from "qrcode";
import LeftArrowIcon from "../icons/LeftArrowIcon";
import { useDispatch } from "../../redux";

interface LoginWindowProps {
  onValidationSuccessfull: () => void;
  onClose: () => void;
}

const maxAttempts = 40;

const LoginWindow = ({
  onValidationSuccessfull,
  onClose,
}: LoginWindowProps): JSX.Element => {
  const dispatch = useDispatch();
  const [qrCodeData, setQrCodeData] = useState<string | undefined>(undefined);
  const intervalRef = useRef<NodeJS.Timer | null>(null);
  const attemptRef = useRef<number>(0);

  const setQrCodeDataAsync = async (url: string): Promise<void> => {
    const qrCodeString = await QRCode.toDataURL(url);
    setQrCodeData(qrCodeString);
  };

  const asyncSignin = async (): Promise<void> => {
    // Fetch data first, then get the one to create the Qrcode and wait for the user connexion with the rest
    const deviceRequestData = await getVerificationUriComplete();
    setQrCodeDataAsync(deviceRequestData.verificationUriComplete);

    intervalRef.current && clearInterval(intervalRef.current);
    intervalRef.current = setInterval(async () => {
      const result = await signinForParameters(
        deviceRequestData.tokenEndpoint,
        deviceRequestData.deviceCode
      );
      if (result) {
        const now = new Date();
        const nowPlusTwoMinutes = new Date(now.getTime() + 2 * 60000);
        dispatch(setParametersOpeningAction(nowPlusTwoMinutes));
        onValidationSuccessfull();
      } else {
        if (attemptRef.current > maxAttempts) {
          onClose();
        } else {
          attemptRef.current++;
        }
      }
    }, 1000);
  };

  useEffect(() => {
    asyncSignin();

    return () => {
      intervalRef.current && clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = (): void => {
    // Refresh the attempts counter and get new qrcode data
    attemptRef.current = 0;
    asyncSignin();
  };

  return (
    <div
      className="absolute z-[100] h-[300px] w-[300px] left-0 bottom-0 flex flex-col p-8 text-white"
      style={{
        background: "rgba(18,18,18,0.7)",
      }}
    >
      {/* Back button */}
      <div
        className="w-full px-2 flex items-center gap-4 uppercase hover:cursor-pointer"
        onClick={onClose}
      >
        <LeftArrowIcon />
        Back
      </div>

      {/* Qr code */}
      <div className="h-full flex flex-col justify-center items-center gap-4">
        <p className="w-full">Use the QRCode below to sign in</p>
        {qrCodeData && (
          <img src={qrCodeData} className="max-h-[150px]" alt="qrcode" />
        )}
        <div
          onClick={handleRefresh}
          className="p-2 px-6 py-2 p-base text-white bg-[#43425D] border border-[#43425D] rounded-sm shadow-md hover:shadow-md hover:bg-white hover:text-[#43425D]"
        >
          Refresh Code
        </div>
      </div>
    </div>
  );
};

export default LoginWindow;
